import './index.css'
import { configure } from 'mobx'
import { setCacheKey } from 'reusable/utils/cache'
import variables from 'variables/variables'
import { reactRender } from 'reusable/react-render/react-render'
import RegionRouter from 'features/main/RegionRouter'
import { Analytics } from 'analytics/analytics'
import { Sentry } from 'sentry/sentry'
import { signIn, signOut, confirmSignIn } from '@aws-amplify/auth'
import { initThemeColors } from '@gap/shared/theme'
import { prefersDarkMode } from 'reusable/utils/utils'
import { canonicalStage } from '@gap/utils/stage'
import { captureAndStripOAuthCallbackSearch } from 'features/calendar/utils/oauth-callback-search'

// Before Sentry.init: session replay records location.href at start-up, independently of beforeSend.
captureAndStripOAuthCallbackSearch();

/** 
 * Export signIn and signOut from amplify
 * If the login request fails, delete the signIn function from the window object
 * This is to prevent the user from trying to login again and again
 */
(window as any).AmplifyAuth = {
   signIn: async ({ username, password }: { username: string, password: string }) => {
      try {
         return await signIn({ username, password })
      } catch (error: any) {
         delete (window as any).AmplifyAuth.signIn
         throw error
      }
   },
   confirmSignInAsEmail: async () => {
      await confirmSignIn({ challengeResponse: 'EMAIL' })
   },
   signOut
}

// Maintenance holding page (NO-416 cutover). When VITE_TEMPORARY_MAINTENANCE_REDIRECT_URL
// is an https URL every visitor is redirected to it, except a tester who has unlocked the
// app with the bypass token. Visit `…/?maintenance_bypass=<token>` once (the token is baked
// from CUTOVER_HOLDING_PAGE_BYPASS_TOKEN) and the grant persists in localStorage, so every
// tab on this browser skips the redirect for the rest of the flip window while everyone else
// still sees the holding page. The stored grant IS the token, so rotating the token
// invalidates every prior bypass. With no token configured (local dev, the dryrun rig) the
// bare `?maintenance_bypass` flag still unlocks, matching the pre-token behaviour. Lower the
// page by redeploying without the redirect URL. The token ships in the client bundle, so it
// is an unguessable knock, not a confidential secret.
const maintenanceRedirectUrl = import.meta.env.VITE_TEMPORARY_MAINTENANCE_REDIRECT_URL
const maintenanceBypassToken = import.meta.env.VITE_MAINTENANCE_BYPASS_TOKEN
const MAINTENANCE_BYPASS_STORAGE_KEY = 'gapMaintenanceBypass'
// The value stored on a successful bypass: the token when one is configured, otherwise a
// constant for the token-less fallback. maintenanceBypassed compares against this same value.
const maintenanceBypassGrant = maintenanceBypassToken || 'bypassed'
let maintenanceBypassed = false
try {
   const currentUrl = new URL(window.location.href)
   const bypassRequested = maintenanceBypassToken
      ? currentUrl.searchParams.get('maintenance_bypass') === maintenanceBypassToken
      : currentUrl.searchParams.has('maintenance_bypass')
   if (bypassRequested) {
      localStorage.setItem(MAINTENANCE_BYPASS_STORAGE_KEY, maintenanceBypassGrant)
      // Strip the token from the address bar so a working link can't be copy-pasted to a client.
      currentUrl.searchParams.delete('maintenance_bypass')
      window.history.replaceState(null, '', currentUrl.pathname + currentUrl.search + currentUrl.hash)
   }
   maintenanceBypassed = localStorage.getItem(MAINTENANCE_BYPASS_STORAGE_KEY) === maintenanceBypassGrant
} catch {
   // localStorage unavailable (private mode / blocked) → no bypass, holding page still shows.
}
if (maintenanceRedirectUrl?.startsWith('https://') && !maintenanceBypassed) {
   window.location.href = maintenanceRedirectUrl // Allow the user to go back and check the app status
} else {
   if (!variables.stage.includes('integration-testing')) {
      Analytics.init()
   }

   Sentry.initialiseIfEnabled()

   // Set document title based on stage
   if (variables.stage.startsWith('dev-')) {
      document.title = `[local] ${variables.gapName}`
   } else if (variables.stage.match(/^(feature|bug|rc)-/)) {
      document.title = `[${variables.stage}] ${variables.gapName}`
   } else if (variables.stage === 'dev') {
      document.title = `[dev] ${variables.gapName}`
   } else if (variables.stage === 'test') {
      document.title = `[test] ${variables.gapName}`
   } else if (canonicalStage(variables.stage) === 'preprod') {
      document.title = `[preprod] ${variables.gapName}`
   }

   setCacheKey(variables.stage)

   // Apply dark primary palette before React renders (reads cached theme mode from localStorage)
   // Default to light mode - dark mode is dev-only feature
   const rawCachedThemeMode = localStorage.getItem('GapAppLocalThemeMode' + variables.stage)
   const cachedThemeMode = rawCachedThemeMode ? JSON.parse(rawCachedThemeMode) : undefined
   const isDark = cachedThemeMode === 'dark' || (cachedThemeMode === 'os-defined' && prefersDarkMode())
   initThemeColors(isDark)

   configure({
      enforceActions: 'never',
   })

   const container = document.getElementById('root')
   reactRender(() => <RegionRouter />, container)
}
